import type { ApiResponse } from '@bower/shared';
import type { HierarchyNode } from '@bower/shared/types/api';
import type { HttpClient } from './http-client.types';

export interface ReorderPayload {
  readonly entityId: string;
  readonly entityType: 'project' | 'artifact';
  readonly afterId?: string;
  readonly beforeId?: string;
}

export function reorderHierarchyItem(
  http: HttpClient,
  payload: ReorderPayload,
): Promise<ApiResponse<{ sortKey: string }>> {
  return http.post<{ sortKey: string }>('/hierarchy/reorder', payload);
}

export interface HierarchyChildrenRequest {
  readonly parentType: 'workspace' | 'project';
  readonly parentId?: string;
  /** `nextCursor` from an earlier page of this same parent. */
  readonly cursor?: string;
}

/**
 * One page of a parent's children. The backend returns child folders plus the
 * first 200 files, then 200 files per page; `nextCursor` is null once the
 * parent is exhausted.
 */
export interface HierarchyChildrenPage {
  readonly nodes: HierarchyNode[];
  readonly nextCursor: string | null;
}

/**
 * GET /hierarchy for one parent, one page. `data` is still the plain children
 * array (so older callers are unaffected); the cursor rides on
 * `meta.nextCursor`, which a backend without paging never sends, so its
 * absence reads as "last page". Throws on a failed envelope rather than
 * returning an empty page, which would render as an empty folder.
 */
export async function getHierarchyChildrenPage(
  http: HttpClient,
  request: HierarchyChildrenRequest,
): Promise<HierarchyChildrenPage> {
  // `paged: '1'` opts in: without it the backend keeps the unpaged contract
  // (full list, error above 5,000) for clients that cannot page.
  const params: Record<string, string> = { parentType: request.parentType, paged: '1' };
  if (request.parentId !== undefined) params.parentId = request.parentId;
  if (request.cursor !== undefined) params.cursor = request.cursor;
  const response = await http.get<HierarchyNode[]>('/hierarchy', { params });
  if (!response.success || !response.data) {
    throw new Error(response.error?.message || 'Failed to fetch hierarchy');
  }
  // `ApiResponse.meta` is typed for the envelope fields only; the hierarchy
  // endpoint adds `nextCursor` beside them.
  const nextCursor = (response.meta as Record<string, unknown> | undefined)?.nextCursor;
  return {
    nodes: response.data,
    nextCursor: typeof nextCursor === 'string' ? nextCursor : null,
  };
}
